// ═══════════════════════════════════════════════════════════════════════════
// grainTargetHistory.js — how far a grain futures contract actually rallies,
// measured CONTRACT BY CONTRACT so a roll can never enter the observation.
//
// ── WHY THIS REPLACES THE CONTINUOUS FILL CURVE ────────────────────────────
// grainTargets.js's grainFillCurve() measures rally distance on the CONTINUOUS
// front-month series (futures_history_daily) and tries to remove the roll
// jumps afterwards with:
//
//     const rolled = s[i].d.slice(0, 7) !== s[i - 1].d.slice(0, 7);
//
// — every change of calendar month. Measured 2026-08-28 against the real
// series, that is not a roll detector at all; it is inverted:
//
//   * CBOT grain contracts stop trading the business day BEFORE the 15th of
//     the delivery month, so the continuous series changes contract mid-month.
//     Recovering the active contract per day (matching the continuous close
//     back to per-contract closes, half-cent tolerance — the Yahoo deep-history
//     seed is rounded to cents while futures_contract_history carries true
//     quarter-cent ticks) puts the roll DAY-OF-MONTH median at 15 for all three
//     crops: corn 21 of 51 rolls on the 15th, soybeans 27 of 70, wheat 26 of 50.
//   * The rule fires on the FIRST trading day of every month — 11.9 times a
//     year, a day on which a roll essentially never happens.
//
// So it discards ~12 innocent returns a year AND leaves every real roll jump
// in the series: median absolute jump 16c corn, 33c soybeans, 21c wheat, on a
// $5-13 board. The code comment claiming the series is "ROLL-FREE" is exactly
// backwards.
//
// ── AND THE FIX IS NOT A BETTER ROLL DETECTOR ──────────────────────────────
// Kevin, 2026-08-28: "Avoid roll detection if possible. Calculate historical
// target behavior from individual contract histories, keeping every observation
// and future target window inside the same contract. That prevents roll jumps
// by construction and is cleaner than trying to repair a continuous series
// afterward."
//
// That is what this file does. Every observation starts and ends inside ONE
// contract_sym, so there is no splice to repair and no transition to identify.
// The empirical recovery above reaches only 74-82% agreement with a documented
// expiry rule and identifies just 1,306-1,754 of 2,545 days confidently — not
// good enough to ship as the sole detector, and this design means we never have
// to.
//
// ── TWO RULES THAT DECIDE THE NUMBERS ──────────────────────────────────────
//   1. A WINDOW MUST BE COMPLETE. An observation is recorded only when the full
//      horizon exists inside the contract. A truncated window can only ever
//      report a SMALLER maximum, so allowing them would bias every rally
//      distance downward — most heavily near expiry, which is exactly where the
//      short-dated boards live.
//   2. NOTHING CROSSES THE CONTRACT'S LAST USABLE DAY. Bars stop at expiry by
//      construction, and `lastUsableDay` is carried so a caller can assert it.
//
// PURE — no fetch, no React, no Supabase, no Date.now()/new Date(). Every
// entry point takes an explicit `asOf`.
// ═══════════════════════════════════════════════════════════════════════════

// Horizons measured, in trading days. Same ladder grainTargets.js already uses,
// so the two are comparable bucket for bucket.
export const HORIZONS = [21, 42, 63, 126, 189, 252];

// MIN_OBSERVATIONS / MIN_CONTRACTS are STATED PARAMETERS, not economic
// thresholds. A horizon is reported only when both are met, and the real counts
// are always returned so a reader can see how thin a bucket is.
//
// ⚠ MIN_CONTRACTS IS 10, AND IT IS THE ONE THAT BITES. Observations inside one
// contract overlap almost completely — a 126-day window starting today and one
// starting tomorrow share 125 of their 126 bars — so the independent sample is
// the CONTRACT, not the observation. Measured 2026-08-28, the 252-day bucket
// looks healthy on observations and is not: corn n=509 from **5 contracts**,
// soybeans n=948 from 8, wheat n=505 from 5. Those five are exactly the
// long-window 2026/27 vintages, because a contract stored as a ~250-bar
// one-year segment cannot contain a single complete 252-day window.
//
// This is the same lesson the futures percentile work already recorded one
// module over (futuresContractHistory.js: "the independent sample is the YEARS,
// not the bars"). At 3 contracts the floor let a five-contract bucket through
// and the walk-forward paid for it — see the header note on horizon
// substitution below.
export const MIN_OBSERVATIONS = 150;
export const MIN_CONTRACTS = 10;

const ymd = (v) => {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

// ── contractLastTradingDay — CBOT grain rule: the business day PRIOR to the
// 15th calendar day of the delivery month. Sourced from the exchange
// convention, and used here ONLY to bound a window and to classify a board as
// expired — never to guess where a spliced series changed contract. That
// distinction matters: bounding is a statement about ONE contract's own life,
// which the contract itself can confirm; roll detection is a statement about a
// splice, which it cannot.
export function contractLastTradingDay(contractDate) {
  const d = ymd(contractDate);
  if (!d) return null;
  const year = Number(d.slice(0, 4)), month = Number(d.slice(5, 7));
  const t = new Date(Date.UTC(year, month - 1, 15));
  t.setUTCDate(t.getUTCDate() - 1);
  while (t.getUTCDay() === 0 || t.getUTCDay() === 6) t.setUTCDate(t.getUTCDate() - 1);
  return t.toISOString().slice(0, 10);
}

// ── normalizeContractBars — one contract's daily bars, sorted, de-duplicated,
// and truncated at `asOf` so a walk-forward caller cannot see the future.
// `reach` decides what counts as having REACHED a level: "close" (the
// convention grainFillCurve already used) or "high" (what actually fills a
// resting sell order). Both are computed by the caller; nothing here assumes
// one, and the choice is reported on the result.
export function normalizeContractBars(bars, { asOf = null } = {}) {
  const seen = new Set();
  const out = [];
  for (const b of bars || []) {
    const d = ymd(b && b.bar_date);
    if (!d || seen.has(d)) continue;
    if (asOf && d > asOf) continue;
    const c = Number(b.close);
    if (!Number.isFinite(c) || c <= 0) continue;
    const h = Number(b.high);
    seen.add(d);
    out.push({ d, c, h: Number.isFinite(h) && h > 0 ? h : c });
  }
  out.sort((a, b) => (a.d < b.d ? -1 : a.d > b.d ? 1 : 0));
  return out;
}

// ── buildContractRallyHistory ──────────────────────────────────────────────
// contracts: [{ sym, contractDate, bars }] — raw rows straight out of
//   futures_contract_history for ONE commodity.
// asOf:      truncate every contract's bars here, and (with expiredOnly) drop
//   contracts that had not finished trading by then. Required for walk-forward.
// expiredOnly: when true, only contracts whose last trading day is strictly
//   BEFORE asOf contribute — the no-lookahead rule. A live contract's own
//   future is exactly what a backtest must not see.
// reach:     'close' | 'high'.
export function buildContractRallyHistory({
  commodity = null, contracts = [], asOf = null, expiredOnly = false,
  horizons = HORIZONS, reach = "close",
  minObservations = MIN_OBSERVATIONS, minContracts = MIN_CONTRACTS,
} = {}) {
  const buckets = {};             // horizon -> sorted gains
  const contribByH = {};          // horizon -> Set(sym)
  const moves = [];               // |daily return|, WITHIN a contract only
  const used = [], skipped = [];

  for (const c of contracts || []) {
    const sym = c && c.sym;
    const ltd = contractLastTradingDay(c && c.contractDate);
    if (!sym) { skipped.push({ sym: null, why: "no contract symbol" }); continue; }
    if (expiredOnly) {
      if (!ltd) { skipped.push({ sym, why: "no delivery date — cannot tell whether it had expired" }); continue; }
      if (!asOf || ltd >= asOf) { skipped.push({ sym, why: "had not stopped trading by asOf — excluded to avoid lookahead" }); continue; }
    }
    const bars = normalizeContractBars(c.bars, { asOf });
    if (bars.length < 40) { skipped.push({ sym, why: `only ${bars.length} bars` }); continue; }
    used.push({ sym, n: bars.length, from: bars[0].d, to: bars[bars.length - 1].d, lastTradingDay: ltd });

    // Daily moves, never across a contract boundary — there is no boundary.
    for (let i = 1; i < bars.length; i++) moves.push(Math.abs(bars[i].c / bars[i - 1].c - 1));

    for (const H of horizons) {
      // COMPLETE WINDOWS ONLY. `i + H <= bars.length - 1` guarantees H forward
      // bars exist inside this same contract; a partial window can only report
      // a smaller maximum and would bias the distribution down.
      for (let i = 0; i + H <= bars.length - 1; i++) {
        const start = bars[i].c;
        if (!(start > 0)) continue;
        let best = -Infinity;
        for (let k = i + 1; k <= i + H; k++) {
          const v = reach === "high" ? bars[k].h : bars[k].c;
          if (v > best) best = v;
        }
        if (!isFinite(best)) continue;
        (buckets[H] = buckets[H] || []).push(best / start - 1);
        (contribByH[H] = contribByH[H] || new Set()).add(sym);
      }
    }
  }

  const coverage = {};
  const have = [];
  for (const H of horizons) {
    const g = buckets[H] || [];
    const nContracts = contribByH[H] ? contribByH[H].size : 0;
    coverage[H] = { observations: g.length, contracts: nContracts,
      usable: g.length >= minObservations && nContracts >= minContracts };
    if (coverage[H].usable) { g.sort((a, b) => a - b); have.push(H); }
  }
  if (!have.length) {
    return {
      known: false, commodity, asOf, reach,
      reason: `no horizon met the floor of ${minObservations} observations across ${minContracts} contracts`,
      coverage, contractsUsed: used, contractsSkipped: skipped, horizonsAvailable: [],
    };
  }

  const sortedMoves = moves.slice().sort((a, b) => a - b);
  // pick() is the nearest AVAILABLE horizon. It is reported on every answer, so
  // a caller can never present a 21-day bucket as a 5-day probability without
  // the substitution being visible.
  //
  // ⚠ SUBSTITUTION IS NOT FREE, AND THE WALK-FORWARD MEASURED THE COST. Early
  // in the backtest few contracts had expired, so the long buckets were
  // unusable and a request for 252 days silently received the 189- or 126-day
  // distribution — a SMALLER gain, so the target was placed nearer and reached
  // more often than claimed. Corrected wheat Target 1 came out at 84.0% actual
  // against 75.0% claimed on the first run, almost entirely from this. Callers
  // must route every reach rate through grainHorizon.js's describeHorizonUse(),
  // which discloses a substitution inside 2x and WITHHOLDS beyond it.
  const pick = (h) => have.reduce((best, x) => (Math.abs(x - h) < Math.abs(best - h) ? x : best), have[0]);

  return {
    known: true, commodity, asOf, reach,
    method: "per-contract, complete windows inside one contract_sym — no roll adjustment, because no splice is crossed",
    typicalMove: sortedMoves.length ? sortedMoves[Math.floor(sortedMoves.length / 2)] : null,
    horizonsAvailable: have,
    coverage,
    contractsUsed: used, contractsSkipped: skipped,
    horizonUsed: pick,
    // The gain a share `p` of historical starting days reached or exceeded.
    gainForFill: (p, h) => {
      const g = buckets[pick(h)];
      return g[Math.max(0, Math.min(g.length - 1, Math.floor((1 - p) * (g.length - 1))))];
    },
    // The share of starting days that reached at least `x`.
    fillForGain: (x, h) => {
      const g = buckets[pick(h)];
      return g.filter((v) => v >= x).length / g.length;
    },
    // Everything a caller needs to STATE the measurement rather than assert it.
    describe: (h) => {
      const u = pick(h);
      return {
        commodity, asOf, reach,
        horizonRequested: h, horizonUsed: u, substituted: u !== h,
        observations: coverage[u].observations, contracts: coverage[u].contracts,
        overlapping: true,
        method: "per-contract complete windows",
        note: u !== h
          ? `no ${h}-day bucket met the sample floor; the ${u}-day bucket was used instead`
          : null,
      };
    },
  };
}
